import { ActionIcon, Box, Button, Group, Table, Text, TextInput } from '@mantine/core';
import { IconPlus, IconTrash } from '@tabler/icons-react';

export type OutputHandle = {
  id: string;
  name: string;
  criteria: string;
};

function isOutputHandle(value: unknown): value is OutputHandle {
  if (!value || typeof value !== 'object') return false;

  const handle = value as Partial<OutputHandle>;

  return typeof handle.id === 'string' && typeof handle.name === 'string' && typeof handle.criteria === 'string';
}

export function readOutputHandles(value: unknown): OutputHandle[] {
  return Array.isArray(value) ? value.filter(isOutputHandle) : [];
}

function createOutputHandle(): OutputHandle {
  return { id: `output-${crypto.randomUUID()}`, name: '', criteria: '' };
}

type OutputHandleEditorProps = {
  handles: OutputHandle[];
  onChange: (handles: OutputHandle[]) => void;
};

export function OutputHandleEditor({ handles, onChange }: OutputHandleEditorProps) {
  const updateHandle = (index: number, updates: Partial<OutputHandle>) => {
    onChange(handles.map((handle, i) => (i === index ? { ...handle, ...updates } : handle)));
  };

  return (
    <>
      {handles.length === 0 ? (
        <Box p="xl" ta="center" c="dimmed">
          <Text>No output handles defined yet.</Text>
          <Text size="sm">Add outputs to create exit points from the AI conversation.</Text>
        </Box>
      ) : (
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Name</Table.Th>
              <Table.Th>Exit Criteria</Table.Th>
              <Table.Th w={60} />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {handles.map((handle, index) => (
              <Table.Tr key={handle.id}>
                <Table.Td>
                  <TextInput
                    placeholder="e.g., Transfer to Agent"
                    value={handle.name}
                    onChange={(e) => updateHandle(index, { name: e.target.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <TextInput
                    placeholder="e.g., When the caller asks to speak with a human"
                    value={handle.criteria}
                    onChange={(e) => updateHandle(index, { criteria: e.target.value })}
                  />
                </Table.Td>
                <Table.Td>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    onClick={() => onChange(handles.filter((_, i) => i !== index))}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}

      <Group>
        <Button
          variant="light"
          size="xs"
          leftSection={<IconPlus size={14} />}
          onClick={() => onChange([...handles, createOutputHandle()])}
        >
          Add Output
        </Button>
      </Group>
    </>
  );
}
